import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import './ProductPage.css';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidRoundedIcon from '@mui/icons-material/AndroidRounded';
import CircularProgress from '@mui/material/CircularProgress';
import { getFullImageUrl } from './imageUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.spayis.com/api';

// Renseigner ces URLs dès que l'app est publiée sur les stores.
const APP_STORE_URL = '';
const PLAY_STORE_URL = '';

function formatPrice(value, currency) {
  return `${Number(value || 0).toLocaleString('fr-FR')} ${currency || 'XAF'}`;
}

export default function ProductPage() {
  const [productId] = useState(() => new URLSearchParams(window.location.search).get('id'));
  const [product, setProduct] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error
  const [loadError, setLoadError] = useState('');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [shareCopied, setShareCopied] = useState(false);
  const [appPromptOpen, setAppPromptOpen] = useState(false);
  const carouselRef = useRef(null);

  const fetchProduct = useCallback(async () => {
    if (!productId) {
      setLoadState('error');
      setLoadError('Aucun produit à afficher.');
      return;
    }
    setLoadState('loading');
    try {
      const res = await fetch(`${API_BASE}/products/public/${encodeURIComponent(productId)}`);
      if (!res.ok) {
        throw new Error(res.status === 404 ? 'Ce produit est introuvable.' : 'Impossible de charger le produit.');
      }
      const data = await res.json();
      setProduct(data);
      setLoadState('ready');
    } catch (err) {
      setLoadError(err.message || 'Une erreur est survenue.');
      setLoadState('error');
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const allImages = useMemo(() => {
    if (!product) return [];
    const extra = (product.images || []).filter((img) => img !== product.mainImage);
    return [...(product.mainImage ? [product.mainImage] : []), ...extra];
  }, [product]);

  const handleCarouselScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActiveImageIndex(idx);
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: product?.name, text: product?.name, url });
      } catch {
        // partage annulé par l'utilisateur
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // clipboard indisponible
    }
  };

  if (loadState === 'loading') {
    return (
      <div className="pp-root pp-center">
        <CircularProgress sx={{ color: '#065F46' }} />
        <p className="pp-muted" style={{ marginTop: 16 }}>Chargement du produit...</p>
      </div>
    );
  }

  if (loadState === 'error' || !product) {
    return (
      <div className="pp-root pp-center">
        <ErrorOutlineRoundedIcon className="pp-error-icon" />
        <h2>Oups</h2>
        <p className="pp-muted">{loadError}</p>
      </div>
    );
  }

  const shop = product.shopId && typeof product.shopId === 'object' ? product.shopId : null;
  const isCertified = !!shop?.isCertified;
  const isDeliverable = !!product.isDeliverable;
  const remise = Number(product.remise) || 0;
  const unitPrice = Number(product.price) || 0;
  const finalPrice = remise > 0 ? Math.round(unitPrice - (unitPrice * remise) / 100) : unitPrice;

  return (
    <div className="pp-root">
      <header className="pp-header">
        <button className="pp-icon-btn" onClick={() => window.history.back()} aria-label="Retour">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </button>
        <span className="pp-header-title">Produit</span>
        <button className="pp-icon-btn" onClick={handleShare} aria-label="Partager">
          <IosShareRoundedIcon fontSize="small" />
        </button>
      </header>

      {shareCopied && <div className="pp-toast">Lien copié !</div>}

      <main className="pp-main">
        <div className="pp-carousel-wrap">
          <div className="pp-carousel" ref={carouselRef} onScroll={handleCarouselScroll}>
            {(allImages.length ? allImages : [null]).map((img, idx) => (
              <div className="pp-carousel-slide" key={idx}>
                {img ? (
                  <img src={getFullImageUrl(img)} alt={product.name} className="pp-carousel-img" />
                ) : (
                  <div className="pp-carousel-placeholder">
                    <ImageNotSupportedRoundedIcon style={{ fontSize: 56, color: '#c0c8d8' }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {product.categoryId?.name && <span className="pp-category-badge">{product.categoryId.name}</span>}

          {allImages.length > 1 && (
            <div className="pp-dots-row">
              {allImages.map((_, idx) => (
                <span key={idx} className={`pp-dot ${idx === activeImageIndex ? 'active' : ''}`} />
              ))}
            </div>
          )}
        </div>

        <div className="pp-body">
          <div className="pp-title-row">
            <div className="pp-title-col">
              {shop?.name && <span className="pp-store-name">{shop.name}</span>}
              <h1 className="pp-product-name">{product.name}</h1>
            </div>
            <div className="pp-price-block">
              {/* {remise > 0 && <span className="pp-price-old">{formatPrice(unitPrice, product.currency)}</span>} */}
              <span className="pp-price-value">{formatPrice(finalPrice, product.currency)}</span>
              {remise > 0 && <span className="pp-price-remise">-{remise}%</span>}
            </div>
          </div>

          {product.description && <p className="pp-description">{product.description}</p>}

          {isDeliverable && Number(product.deliveryFees) > 0 && (
            <div className="pp-fee-row">
              <LocalShippingRoundedIcon fontSize="small" />
              <span>Livraison : {formatPrice(product.deliveryFees, product.currency)}</span>
            </div>
          )}

          {shop && (
            <div className="pp-seller-row">
              <div className="pp-seller-avatar">
                {shop.mainPhoto || shop.logo ? (
                  <img src={getFullImageUrl(shop.logo || shop.mainPhoto)} alt={shop.name} />
                ) : (
                  <StorefrontRoundedIcon style={{ color: '#065F46' }} />
                )}
              </div>
              <div className="pp-seller-info">
                <span className="pp-seller-name">
                  {shop.name}
                  {shop.isCertified && <VerifiedRoundedIcon fontSize="inherit" className="pp-seller-cert" />}
                </span>
              </div>
              {shop.invitationCode && (
                <a className="pp-seller-link" href={`/boutique?code=${encodeURIComponent(shop.invitationCode)}`}>
                  Voir la boutique
                </a>
              )}
            </div>
          )}

          <div className="pp-trust-row">
            {shop.isCertified ? (
              <div className="pp-trust-card pp-trust-certified">
                <ShieldRoundedIcon fontSize="small" />
                <span>Authenticité garantie</span>
              </div>
            ) : (
              <div className="pp-trust-card pp-trust-warning">
                <WarningAmberRoundedIcon fontSize="small" />
                <span>Boutique non certifiée</span>
              </div>
            )}
            <div className={`pp-trust-card ${isDeliverable ? 'pp-trust-deliverable' : 'pp-trust-nodelivery'}`}>
              {isDeliverable ? <LocalShippingRoundedIcon fontSize="small" /> : <StoreRoundedIcon fontSize="small" />}
              <span>{isDeliverable ? 'Livrable' : 'Retrait en boutique'}</span>
            </div>
          </div>
        </div>
      </main>

      <div className="pp-cta-bar">
        <button className="pp-cta-btn" onClick={() => setAppPromptOpen(true)}>
          <CreditCardRoundedIcon fontSize="small" /> Payer
        </button>
      </div>

      {appPromptOpen && (
        <div className="pp-sheet-overlay" onClick={() => setAppPromptOpen(false)}>
          <div className="pp-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="pp-sheet-handle" />
            <button className="pp-sheet-close" onClick={() => setAppPromptOpen(false)} aria-label="Fermer">
              <CloseRoundedIcon fontSize="small" />
            </button>

            <div className="pp-app-prompt">
              <div className="pp-app-icon-circle">
                <CreditCardRoundedIcon style={{ fontSize: 32, color: 'white' }} />
              </div>
              <h3>Payez via l'application Spayis</h3>
              <p className="pp-muted">
                Pour finaliser votre paiement (comptant ou par tranches), téléchargez l'application mobile Spayis.
              </p>

              <div className="pp-store-buttons">
                {APP_STORE_URL ? (
                  <a className="pp-store-btn" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                    <AppleIcon fontSize="small" /> App Store
                  </a>
                ) : (
                  <span className="pp-store-btn pp-store-btn-disabled">
                    <AppleIcon fontSize="small" /> App Store — Bientôt disponible
                  </span>
                )}
                {PLAY_STORE_URL ? (
                  <a className="pp-store-btn" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                    <AndroidRoundedIcon fontSize="small" /> Google Play
                  </a>
                ) : (
                  <span className="pp-store-btn pp-store-btn-disabled">
                    <AndroidRoundedIcon fontSize="small" /> Google Play — Bientôt disponible
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
